import React, { useState, useMemo, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  generateFeatureInquiryMessage,
  generateFeatureInquirySubject,
} from "../../config/featurePromotions";
import "../../App.css";

/**
 * EnableFeatureModal
 * Completely generic, highly flexible marketing modal for promoting and unlocking
 * enterprise or gated capabilities anywhere across GradeMe AI.
 *
 * - Zero hardcoded domain data (100% props-driven)
 * - Standardized inquiry message template for both WhatsApp and Email
 * - Fluid Framer Motion animations with layoutId tab sliding
 * - Strictly Light Mode & Solid Brand Colors (--color-1, --color-2, --color-5)
 * - Compact single-viewport layout (zero overflow scrolling on standard screens)
 */
export default function EnableFeatureModal({
  isOpen,
  onClose,
  badgeText = "EXCLUSIVE FEATURE",
  badgeIcon = "fa-solid fa-lock",
  tagline = "GradeMe AI Suite",
  title = "Unlock Premium Capability",
  subtitle = "Enable this feature for your institute to elevate your academic workflows.",
  interactiveTabs = [],
  benefits = [],
  instituteName = "Our Institute",
  instituteCode = "",
  userName = "Instructor",
  userEmail = "",
  userRole = "instructor",
  featureName = "Library Bank",
  customMessageTemplate = null,
  whatsappNumber = "919381369640",
  supportEmail = "support@grademe-ai.com",
}) {
  const [activeTabId, setActiveTabId] = useState(
    interactiveTabs && interactiveTabs.length > 0 ? interactiveTabs[0].id : null,
  );
  const [copied, setCopied] = useState(false);

  const cleanInstitute = useMemo(() => {
    return instituteName?.trim() || "Our Institute";
  }, [instituteName]);

  const cleanPhone = useMemo(() => {
    return (whatsappNumber || "919381369640").replace(/[^\d]/g, "");
  }, [whatsappNumber]);

  // Find currently active tab data
  const currentTab = useMemo(() => {
    if (!interactiveTabs || interactiveTabs.length === 0) return null;
    return (
      interactiveTabs.find((t) => t.id === activeTabId) || interactiveTabs[0]
    );
  }, [interactiveTabs, activeTabId]);

  // Reusable structured lead message for both WhatsApp and Email
  const leadMessage = useMemo(() => {
    if (typeof customMessageTemplate === "function") {
      return customMessageTemplate({
        featureName,
        instituteName: cleanInstitute,
        instituteCode,
        userName,
        userEmail,
        userRole,
      });
    }
    return generateFeatureInquiryMessage({
      featureName,
      instituteName: cleanInstitute,
      instituteCode,
      userName,
      userEmail,
      userRole,
    });
  }, [
    customMessageTemplate,
    featureName,
    cleanInstitute,
    instituteCode,
    userName,
    userEmail,
    userRole,
  ]);

  const emailSubject = useMemo(() => {
    return generateFeatureInquirySubject({
      featureName,
      instituteName: cleanInstitute,
    });
  }, [featureName, cleanInstitute]);

  const whatsappUrl = `https://wa.me/${cleanPhone}?text=${encodeURIComponent(leadMessage)}`;
  const mailtoUrl = `mailto:${supportEmail}?subject=${encodeURIComponent(emailSubject)}&body=${encodeURIComponent(leadMessage)}`;
  const gmailUrl = `https://mail.google.com/mail/?view=cm&fs=1&to=${encodeURIComponent(supportEmail)}&su=${encodeURIComponent(emailSubject)}&body=${encodeURIComponent(leadMessage)}`;

  const handleEmailClick = useCallback(
    (e) => {
      // On mobile devices, mailto: opens the device's native mail app with prefilled draft
      const isMobile =
        typeof navigator !== "undefined" &&
        /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);

      if (isMobile) {
        e.preventDefault();
        window.location.href = mailtoUrl;
      }
      // On desktop, the natural link href={gmailUrl} opens Gmail in a new tab and auto-saves draft
    },
    [mailtoUrl]
  );

  const handleCopyLeadMessage = useCallback(() => {
    if (navigator.clipboard) {
      navigator.clipboard.writeText(leadMessage);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    }
  }, [leadMessage]);

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <div className="efm-overlay" onClick={onClose} role="dialog" aria-modal="true">
        <motion.div
          className="efm-modal"
          onClick={(e) => e.stopPropagation()}
          initial={{ opacity: 0, scale: 0.94, y: 14 }}
          animate={{
            opacity: 1,
            scale: 1,
            y: 0,
            transition: { type: "spring", damping: 28, stiffness: 380, mass: 0.8 },
          }}
          exit={{ opacity: 0, scale: 0.94, y: 14, transition: { duration: 0.16 } }}
        >
          {/* Top Solid Brand Stripe */}
          <div className="efm-top-stripe" />

          {/* Close Button */}
          <button
            type="button"
            className="efm-close-btn"
            onClick={onClose}
            aria-label="Close dialog"
          >
            ×
          </button>

          {/* Modal Content */}
          <div className="efm-content">
            {/* Header */}
            <div className="efm-header">
              <div className="efm-badge-row">
                <span className="efm-badge-solid">
                  {badgeIcon && <i className={badgeIcon} />}
                  {badgeText}
                </span>
                {tagline && (
                  <span className="efm-tagline-solid">
                    <i className="fa-solid fa-sparkles" style={{ color: "var(--color-1)" }} />
                    {tagline}
                  </span>
                )}
              </div>

              <h2 className="efm-title">{title}</h2>
              {subtitle && <p className="efm-subtitle">{subtitle}</p>}
            </div>

            {/* Optional Interactive Tabs Exploration */}
            {interactiveTabs && interactiveTabs.length > 0 && currentTab && (
              <div className="efm-interactive-section">
                {/* Fluid Pill Tab Switcher */}
                <div className="efm-tabs" role="tablist">
                  {interactiveTabs.map((tab) => {
                    const isActive = (activeTabId || interactiveTabs[0].id) === tab.id;
                    return (
                      <button
                        key={tab.id}
                        type="button"
                        role="tab"
                        aria-selected={isActive}
                        className={`efm-tab ${isActive ? "efm-tab-active" : ""}`}
                        onClick={() => setActiveTabId(tab.id)}
                      >
                        {isActive && (
                          <motion.div
                            layoutId="efmActiveTabIndicator"
                            className="efm-tab-indicator"
                            transition={{ type: "spring", stiffness: 450, damping: 32 }}
                          />
                        )}
                        <span className="efm-tab-label">
                          {tab.icon && <i className={tab.icon} />}
                          <span>{tab.label}</span>
                        </span>
                      </button>
                    );
                  })}
                </div>

                {/* Animated Showcase Panel */}
                <AnimatePresence mode="wait">
                  <motion.div
                    key={currentTab.id}
                    className="efm-tab-panel"
                    initial={{ opacity: 0, y: 6 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -6 }}
                    transition={{ duration: 0.16 }}
                  >
                    <div className="efm-panel-main">
                      <div className="efm-panel-top-row">
                        <span className="efm-panel-headline">
                          {currentTab.headline || currentTab.label}
                        </span>
                        {currentTab.badge && (
                          <span className="efm-panel-badge">{currentTab.badge}</span>
                        )}
                      </div>

                      {currentTab.subjects && (
                        <span className="efm-panel-subjects">{currentTab.subjects}</span>
                      )}

                      {currentTab.highlights && currentTab.highlights.length > 0 && (
                        <div className="efm-panel-checks">
                          {currentTab.highlights.map((h, i) => (
                            <div key={i} className="efm-panel-check-item">
                              <i
                                className="fa-solid fa-circle-check"
                                style={{ color: "var(--color-1)", fontSize: "0.76rem" }}
                              />
                              <span>{h}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </motion.div>
                </AnimatePresence>
              </div>
            )}

            {/* Optional 3 Compact Benefit Cards */}
            {benefits && benefits.length > 0 && (
              <div className="efm-benefits-row">
                {benefits.map((b, idx) => (
                  <motion.div
                    key={idx}
                    className="efm-benefit-box"
                    whileHover={{ y: -2 }}
                    transition={{ duration: 0.15 }}
                  >
                    <div className="efm-benefit-icon-row">
                      <div className={`efm-benefit-icon ${idx === 1 ? "color-2" : ""}`}>
                        {b.icon && <i className={b.icon} />}
                      </div>
                      <span className="efm-benefit-title">{b.title}</span>
                    </div>
                    {b.desc && <span className="efm-benefit-desc">{b.desc}</span>}
                  </motion.div>
                ))}
              </div>
            )}

            {/* Action Dock Footer (Compact Row) */}
            <div className="efm-action-dock">
              <div className="efm-action-lead">
                <div className="efm-action-heading">
                  Ready to activate for{" "}
                  <span className="efm-action-inst-name">{cleanInstitute}</span>?
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
                  <button
                    type="button"
                    className="efm-action-copy-link"
                    onClick={handleCopyLeadMessage}
                  >
                    <i className={copied ? "fa-solid fa-check" : "fa-regular fa-clone"} />
                    <span>
                      {copied ? "Inquiry text copied to clipboard!" : "Copy pre-filled inquiry text"}
                    </span>
                  </button>
                  <span style={{ color: "#cbd5e1" }}>•</span>
                  <a
                    href={mailtoUrl}
                    className="efm-action-copy-link"
                    title="Open in your default desktop mail application (e.g. Outlook, Apple Mail)"
                  >
                    <i className="fa-solid fa-arrow-up-right-from-square" style={{ fontSize: "0.65rem" }} />
                    <span>Desktop Mail App</span>
                  </a>
                </div>
              </div>

              <div className="efm-action-btns">
                {/* WhatsApp Direct CTA */}
                <motion.a
                  href={whatsappUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="efm-btn-solid-wa"
                  whileHover={{ y: -1, scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <i className="fa-brands fa-whatsapp" style={{ fontSize: "1.05rem" }} />
                  <span>Chat on WhatsApp</span>
                </motion.a>

                {/* Email Sales Team CTA (Opens Gmail web compose in new tab & auto-saves draft; native mail on mobile) */}
                <motion.a
                  href={gmailUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="efm-btn-solid-email"
                  onClick={handleEmailClick}
                  whileHover={{ y: -1, scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  title="Opens email draft to support@grademe-ai.com"
                >
                  <i className="fa-solid fa-envelope" />
                  <span>Email Sales Team</span>
                </motion.a>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
}
